/**
 * GP HTL Payment Chain Tab
 * Shows payment chains with status tracking from customer to GPHTL
 */
import React, { useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Stack,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Collapse,
  Typography,
  Divider,
  Card,
  CardContent,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
  CheckCircle as CheckIcon,
  Schedule as PendingIcon,
  Warning as WarningIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';
import { useGphtlPaymentChains } from '../../../../../hooks/useGphtl';
import { GphtlPaymentChain, GphtlPaymentChainFilters } from '../../../../../types/gphtl.types';

// Format currency for display
const formatCurrency = (amount: number | undefined): string => {
  if (amount === undefined || amount === null) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

// Format date for display
const formatDate = (dateString: string | undefined): string => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

// Get status chip color
const getChainStatusColor = (status: string): 'success' | 'warning' | 'info' | 'error' | 'default' => {
  switch (status) {
    case 'complete':
      return 'success';
    case 'partial':
      return 'warning';
    case 'pending':
      return 'info';
    case 'error':
      return 'error';
    default:
      return 'default';
  }
};

// Payment chain step component
interface ChainStepProps {
  label: string;
  amount: number;
  paid: boolean;
  paidDate?: string;
  paidAmount?: number;
}

const ChainStep: React.FC<ChainStepProps> = ({ label, amount, paid, paidDate, paidAmount }) => (
  <Box
    sx={{
      p: 2,
      border: 1,
      borderColor: paid ? 'success.main' : 'grey.300',
      borderRadius: 1,
      bgcolor: paid ? 'success.light' : 'grey.50',
      minWidth: 150,
      textAlign: 'center',
    }}
  >
    <Typography variant="subtitle2" gutterBottom>
      {label}
    </Typography>
    <Typography variant="h6">{formatCurrency(amount)}</Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 1 }}>
      {paid ? (
        <>
          <CheckIcon color="success" fontSize="small" sx={{ mr: 0.5 }} />
          <Typography variant="caption" color="success.main">
            PAID {paidDate ? formatDate(paidDate) : ''}
          </Typography>
        </>
      ) : (
        <>
          <PendingIcon color="action" fontSize="small" sx={{ mr: 0.5 }} />
          <Typography variant="caption" color="text.secondary">
            PENDING
          </Typography>
        </>
      )}
    </Box>
    {paidAmount && paidAmount !== amount && (
      <Typography variant="caption" color="text.secondary">
        Paid: {formatCurrency(paidAmount)}
      </Typography>
    )}
  </Box>
);

// Expandable row component
interface ExpandableRowProps {
  chain: GphtlPaymentChain;
}

const ExpandableRow: React.FC<ExpandableRowProps> = ({ chain }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow hover sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{chain.deal_id}</TableCell>
        <TableCell align="right">{formatCurrency(chain.original_amount)}</TableCell>
        <TableCell align="center">
          {chain.customer_paid ? (
            <CheckIcon color="success" fontSize="small" />
          ) : (
            <PendingIcon color="action" fontSize="small" />
          )}
        </TableCell>
        <TableCell align="center">
          {chain.b2s_paid ? (
            <CheckIcon color="success" fontSize="small" />
          ) : (
            <PendingIcon color="action" fontSize="small" />
          )}
        </TableCell>
        <TableCell align="center">
          {chain.gphtl_paid ? (
            <CheckIcon color="success" fontSize="small" />
          ) : (
            <PendingIcon color="action" fontSize="small" />
          )}
        </TableCell>
        <TableCell>
          <Chip
            label={chain.chain_status}
            size="small"
            color={getChainStatusColor(chain.chain_status)}
          />
        </TableCell>
        <TableCell>{formatDate(chain.created_at)}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ m: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Payment Chain Details
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {/* Visual Payment Chain */}
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="center"
                sx={{ my: 2, flexWrap: 'wrap' }}
              >
                <ChainStep
                  label="Customer Invoice"
                  amount={chain.original_amount}
                  paid={chain.customer_paid}
                  paidDate={chain.customer_paid_date}
                  paidAmount={chain.customer_paid_amount}
                />
                <ArrowIcon color="action" />
                {chain.b2s_amount && (
                  <>
                    <ChainStep
                      label="B2S Bill"
                      amount={chain.b2s_amount}
                      paid={chain.b2s_paid}
                      paidDate={chain.b2s_paid_date}
                      paidAmount={chain.b2s_paid_amount}
                    />
                    <ArrowIcon color="action" />
                  </>
                )}
                <ChainStep
                  label="GPHTL Bill"
                  amount={chain.gphtl_amount}
                  paid={chain.gphtl_paid}
                  paidDate={chain.gphtl_paid_date}
                  paidAmount={chain.gphtl_paid_amount}
                />
              </Stack>

              {/* Additional Details */}
              <Card variant="outlined" sx={{ mt: 2 }}>
                <CardContent>
                  <Stack direction="row" spacing={4} flexWrap="wrap">
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Mapping ID
                      </Typography>
                      <Typography variant="body2">{chain.mapping_id}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Original Amount
                      </Typography>
                      <Typography variant="body2">{formatCurrency(chain.original_amount)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        GPHTL Amount (80%)
                      </Typography>
                      <Typography variant="body2">{formatCurrency(chain.gphtl_amount)}</Typography>
                    </Box>
                    {chain.b2s_amount && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          B2S Amount (95%)
                        </Typography>
                        <Typography variant="body2">{formatCurrency(chain.b2s_amount)}</Typography>
                      </Box>
                    )}
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Last Updated
                      </Typography>
                      <Typography variant="body2">{formatDate(chain.updated_at)}</Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export const GphtlPaymentChainTab: React.FC = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<GphtlPaymentChainFilters>({});

  const { data, isLoading, error, refetch } = useGphtlPaymentChains(
    { ...filters, search: searchQuery },
    page + 1,
    rowsPerPage
  );

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handleFilterChange = (field: keyof GphtlPaymentChainFilters, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value || undefined,
    }));
    setPage(0);
  };

  const clearFilters = () => {
    setFilters({});
    setSearchQuery('');
    setPage(0);
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load payment chains: {error.message}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Search and Filters */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }} alignItems="center">
        <TextField
          placeholder="Search by deal ID or name..."
          value={searchQuery}
          onChange={handleSearchChange}
          size="small"
          sx={{ minWidth: 300 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={filters.status || ''}
            label="Status"
            onChange={(e) => handleFilterChange('status', e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="complete">Complete</MenuItem>
            <MenuItem value="partial">Partial</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="error">Error</MenuItem>
          </Select>
        </FormControl>
        <TextField
          label="From Date"
          type="date"
          size="small"
          value={filters.dateFrom || ''}
          onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 150 }}
        />
        <TextField
          label="To Date"
          type="date"
          size="small"
          value={filters.dateTo || ''}
          onChange={(e) => handleFilterChange('dateTo', e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 150 }}
        />
        <IconButton onClick={() => refetch()} size="small" title="Refresh">
          <RefreshIcon />
        </IconButton>
        {(Object.keys(filters).length > 0 || searchQuery) && (
          <Button variant="text" size="small" onClick={clearFilters}>
            Clear
          </Button>
        )}
      </Stack>

      {/* Payment Chains Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={50} />
              <TableCell>Deal ID</TableCell>
              <TableCell align="right">Original Amount</TableCell>
              <TableCell align="center">Customer Paid</TableCell>
              <TableCell align="center">B2S Paid</TableCell>
              <TableCell align="center">GPHTL Paid</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : !data?.data || data.data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    No payment chains found.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              data.data.map((chain: GphtlPaymentChain) => (
                <ExpandableRow key={chain.chain_id} chain={chain} />
              ))
            )}
          </TableBody>
        </Table>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={data?.totalCount || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </Box>
  );
};

export default GphtlPaymentChainTab;
